import { useEffect, useMemo, useRef, useState } from "react";
import { Link } from "@/components/LangLink";
import { motion, useScroll, useTransform, AnimatePresence } from "framer-motion";
import { ArrowRight, Clock, Globe2, ShieldCheck, Sparkles, ChevronDown, ChevronLeft, ChevronRight } from "lucide-react";
import { MaskedLine, Reveal, Overline } from "@/components/Reveal";
import EditorialMarquee from "@/components/Marquee";
import ImageFader from "@/components/ImageFader";
import Testimonials from "@/components/Testimonials";
import { IMAGES, SERVICES, MENU_COLUMNS } from "@/data/site";
import { tr } from "@/i18n";

const ASSURANCES = [
  { icon: Clock, title: "Around the Clock", text: "24-hour access, every day of the year" },
  { icon: Globe2, title: "Global Network", text: "25+ destinations, one point of contact" },
  { icon: ShieldCheck, title: "Absolute Discretion", text: "Your privacy is our priority" },
  { icon: Sparkles, title: "Members Only", text: "Access beyond the reach of money" },
];

const HOME_SERVICES = MENU_COLUMNS.flat().map((sid) => SERVICES.find((s) => s.id === sid));

const WELCOME_SLIDES = [
  "/images/rot-2.jpeg",
  "/images/rot-7.jpeg",
  "/images/rot-9.jpeg",
  "/images/rot-8.jpeg",
  "/images/rot-1.jpeg",
];

const PROMISE_SLIDES = [
  "/images/rot-3.jpeg",
  "/images/rot-4.jpeg",
  "/images/rot-5.jpeg",
  "/images/rot-6.jpeg",
];
const HERO_ROTATION_IMAGES = [
  { src: "/images/hero-rotation/01.jpg", alt: "Luxury chauffeur vehicle interior" },
  { src: "/images/hero-rotation/02.jpg", alt: "Aerial view of a private island resort" },
  { src: "/images/hero-rotation/03.jpg", alt: "Palm-fringed private resort pool" },
  { src: "/images/hero-rotation/04.jpg", alt: "Overwater villas and private boardwalk" },
  { src: "/images/hero-rotation/05.jpg", alt: "Aerial turquoise beach and jetty" },
  { src: "/images/hero-rotation/06.jpg", alt: "Yellow Lamborghini outside a luxury hotel" },
  { src: "/images/hero-rotation/07.jpg", alt: "Elegant private dining event" },
  { src: "/images/hero-rotation/08.jpg", alt: "Formula One car on a racetrack" },
  { src: "/images/hero-rotation/09.jpg", alt: "Luxury fashion presentation" },
  { src: "/images/hero-rotation/10.jpg", alt: "Executive chauffeur cabin detail" },
];

const shuffleImages = (images) => {
  const copy = [...images];
  for (let i = copy.length - 1; i > 0; i -= 1) {
    const j = Math.floor(Math.random() * (i + 1));
    [copy[i], copy[j]] = [copy[j], copy[i]];
  }
  return copy;
};


export default function Home() {
  const heroRef = useRef(null);
  const { scrollYProgress } = useScroll({ target: heroRef, offset: ["start start", "end start"] });
  const heroY = useTransform(scrollYProgress, [0, 1], ["0%", "25%"]);
  const heroOpacity = useTransform(scrollYProgress, [0, 0.8], [1, 0]);
  const carouselRef = useRef(null);
  const scrollCarousel = (dir) => carouselRef.current?.scrollBy({ left: dir * (document.documentElement.dir === "rtl" ? -352 : 352), behavior: "smooth" });
  const heroImages = useMemo(
    () => [{ src: "/images/hero-main.webp", alt: "Private turquoise beach" }, ...shuffleImages(HERO_ROTATION_IMAGES)],
    []
  );
  const [heroIndex, setHeroIndex] = useState(0);
  const [heroPlaying, setHeroPlaying] = useState(true);

  useEffect(() => {
    heroImages.forEach(({ src }) => {
      const image = new Image();
      image.src = src;
    });
  }, [heroImages]);

  useEffect(() => {
    if (!heroPlaying) return undefined;
    const interval = window.setInterval(() => setHeroIndex((current) => (current + 1) % heroImages.length), 5200);
    return () => window.clearInterval(interval);
  }, [heroPlaying, heroImages.length]);

  const chooseHeroImage = (index) => {
    setHeroIndex(index);
    setHeroPlaying(false);
  };


  return (
    <div data-testid="home-page">
      {/* ——— KINETIC HERO ——— */}
      <section ref={heroRef} className="relative min-h-[100svh] overflow-hidden grain">
        <motion.div style={{ y: heroY }} className="absolute inset-0 -bottom-40">
          <div data-testid="hero-rotation-image" data-active-src={heroImages[heroIndex].src} className="absolute inset-0">
            <AnimatePresence initial={false}>
              <motion.img
                key={heroImages[heroIndex].src}
                src={heroImages[heroIndex].src}
                alt={tr(heroImages[heroIndex].alt)}
                className="absolute inset-0 w-full h-full object-cover brightness-110"
                initial={{ opacity: 0, scale: 1.015 }}
                animate={{ opacity: 1, scale: 1 }}
                exit={{ opacity: 0 }}
                transition={{ duration: 1.1, ease: [0.16, 1, 0.3, 1] }}
              />
            </AnimatePresence>
          </div>
          <div className="absolute inset-0 bg-gradient-to-t from-ink/95 via-ink/35 to-ink/10" />
          <div className="absolute inset-0 bg-gradient-to-r from-ink/60 via-ink/25 to-transparent" />
        </motion.div>

        <motion.div style={{ opacity: heroOpacity }} className="relative min-h-[100svh] max-w-[110rem] mx-auto px-6 lg:px-12 flex flex-col justify-end pt-28 pb-20 lg:pb-28">
          <MaskedLine delay={0.2}>
            <span className="overline-lux">{tr("Luxury Lifestyle Management · Est. London")}</span>
          </MaskedLine>
          <h1 className="font-serif font-light leading-[0.98] tracking-tight mt-6 text-[13vw] sm:text-7xl md:text-8xl lg:text-[7.5rem]">
            <MaskedLine delay={0.35}>
              <span className="text-white">{tr("Extraordinary living,")}</span>
            </MaskedLine>
            <MaskedLine delay={0.55}>
              <em className="gold-shimmer italic">{tr("effortlessly arranged.")}</em>
            </MaskedLine>
          </h1>
          <MaskedLine delay={0.8}>
            <span className="block mt-8 max-w-xl text-white/60 font-light leading-relaxed text-base md:text-lg">{tr("Delux Concierge is a premium global concierge service dedicated to simplifying the lives of our clients through seamless access to exceptional services, experiences and solutions. From travel and lifestyle to bespoke arrangements, we manage every detail with speed, discretion and uncompromising attention to quality — delivering a seamless experience without ever compromising on luxury.")}</span>
          </MaskedLine>
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 1.05, duration: 0.8 }}
            className="mt-12 flex flex-wrap gap-5"
          >
            <Link
              to="/services"
              data-testid="hero-explore-services-button"
              className="group bg-gold text-black text-xs uppercase tracking-[0.3em] px-9 py-4 flex items-center gap-3 hover:bg-gold-bright transition-colors duration-400"
            >{tr("Explore Services")}<ArrowRight strokeWidth={1.5} size={15} className="transition-transform duration-400 group-hover:translate-x-1" />
            </Link>
            <Link
              to="/membership"
              data-testid="hero-membership-button"
              className="border border-white/30 text-white text-xs uppercase tracking-[0.3em] px-9 py-4 hover:border-gold hover:text-gold transition-colors duration-400"
            >{tr("Request Admission")}</Link>
          </motion.div>
        </motion.div>

        <div className="absolute z-20 left-6 lg:left-12 bottom-8 flex items-center gap-2.5" data-testid="hero-rotation-controls">
          {heroImages.map((image, index) => (
            <button
              key={image.src}
              type="button"
              data-testid={`hero-dot-${index + 1}`}
              aria-label={tr("View hero image {{number}}", { number: index + 1 })}
              onClick={() => chooseHeroImage(index)}
              className={`h-1.5 rounded-full transition-all duration-500 ${index === heroIndex ? "w-7 bg-gold" : "w-1.5 bg-white/35 hover:bg-white/75"}`}
            />
          ))}
        </div>

        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 1.6, duration: 1 }}
          className="absolute bottom-8 left-1/2 -translate-x-1/2 text-white/40"
        >
          <ChevronDown strokeWidth={1} size={26} className="animate-bounce" />
        </motion.div>
      </section>

      {/* ——— ASSURANCE STRIP ——— */}
      <section className="border-b border-white/10 bg-[#070707]">
        <div className="max-w-[110rem] mx-auto px-6 lg:px-12 py-10 flex flex-wrap items-center justify-between gap-x-10 gap-y-8">
          {ASSURANCES.map((a, i) => (
            <Reveal key={a.title} delay={i * 0.08} className="flex items-center gap-4">
              <a.icon strokeWidth={1} size={22} className="text-gold shrink-0" />
              <div>
                <p className="text-[0.62rem] uppercase tracking-[0.28em] text-white">{tr(a.title)}</p>
                <p className="text-xs text-white/40 font-light mt-1.5">{tr(a.text)}</p>
              </div>
            </Reveal>
          ))}
        </div>
      </section>

      {/* ——— EDITORIAL MARQUEE ——— */}
      <EditorialMarquee />

      {/* ——— WELCOME / ABOUT INTRO (ivory, navy logo) ——— */}
      <section className="bg-ivory text-navy relative overflow-hidden">
        <div className="max-w-7xl mx-auto px-6 lg:px-12 py-28 lg:py-40 grid grid-cols-1 lg:grid-cols-12 gap-16 items-center">
          <div className="lg:col-span-7">
            <Reveal>
              <p className="text-[0.7rem] uppercase tracking-[0.35em] text-navy/60 font-medium">{tr("Welcome to Delux")}</p>
            </Reveal>
            <Reveal delay={0.1}>
              <h2 className="font-serif font-light text-4xl sm:text-5xl lg:text-6xl leading-[1.05] mt-6 text-navy">{tr("Every wish,")}<em className="italic">{tr("quietly fulfilled.")}</em>
              </h2>
            </Reveal>
            <Reveal delay={0.2}>
              <p className="mt-8 text-navy/70 font-light leading-relaxed max-w-xl">{tr("At Delux Concierge, we are committed to delivering exceptional service with the highest standards of quality, discretion and attention to detail. With extensive experience across the luxury services sector, our team takes the time to understand each client's individual requirements and ensures every arrangement is executed with precision. From the initial request through to completion, we remain focused on delivering the right solution, efficiently and without compromising on the quality of the experience.")}</p>
            </Reveal>
            <Reveal delay={0.3}>
              <Link
                to="/about"
                data-testid="home-about-link"
                className="group inline-flex items-center gap-3 mt-10 text-xs uppercase tracking-[0.3em] text-navy border-b border-navy/40 pb-2 hover:border-navy transition-colors duration-400"
              >{tr("Our Story")}<ArrowRight strokeWidth={1.5} size={14} className="transition-transform duration-400 group-hover:translate-x-1" />
              </Link>
            </Reveal>
          </div>
          <div className="lg:col-span-5 relative">
            <Reveal delay={0.15}>
              <ImageFader images={WELCOME_SLIDES} alt={tr("Luxury lifestyle")} className="aspect-[3/4]" testIdPrefix="welcome-slide" />
            </Reveal>
          </div>
        </div>
      </section>

      {/* ——— SERVICES CAROUSEL ——— */}
      <section className="bg-[#EDE5D3] text-navy py-24 lg:py-36">
        <div className="max-w-7xl mx-auto px-6 lg:px-12 text-center mb-16">
          <Reveal>
            <p className="text-[0.7rem] uppercase tracking-[0.35em] text-navy/50 font-medium">{tr("What We Arrange")}</p>
          </Reveal>
          <Reveal delay={0.1}>
            <h2 className="font-serif font-light text-4xl sm:text-5xl lg:text-6xl mt-5">{tr("Our")}<em className="italic">{tr("Services.")}</em>
            </h2>
          </Reveal>
        </div>
        <div className="relative max-w-[110rem] mx-auto px-6 lg:px-20">
          <button
            data-testid="services-carousel-prev"
            onClick={() => scrollCarousel(-1)}
            aria-label={tr("Previous services")}
            className="hidden lg:flex absolute left-6 top-[38%] z-10 w-12 h-12 rounded-full bg-white shadow-xl items-center justify-center text-navy hover:bg-gold hover:text-black transition-colors duration-300"
          >
            <ChevronLeft strokeWidth={1.5} size={20} />
          </button>
          <div ref={carouselRef} data-testid="services-carousel" className="flex gap-6 overflow-x-auto snap-x no-scrollbar">
            {HOME_SERVICES.map((s, i) => (
              <Reveal key={s.id} delay={Math.min(i, 4) * 0.08} className="snap-start shrink-0 w-[270px] sm:w-[320px]">
                <Link to={`/services/${s.id}`} data-testid={`home-service-${s.id}`} className="group block">
                  <div className="relative aspect-[3/4] overflow-hidden">
                    <img
                      src={s.image}
                      alt={tr(s.name)}
                      className="w-full h-full object-cover transition-transform duration-[1.3s] ease-out group-hover:scale-105"
                    />
                    <div className="absolute inset-0 bg-gradient-to-t from-black/75 via-black/10 to-transparent" />
                    <div className="absolute bottom-0 inset-x-0 p-6">
                      <h3 className="font-serif text-2xl font-light text-white">{tr(s.name)}</h3>
                      <p className="text-white/55 text-xs font-light mt-1.5 leading-relaxed">{tr(s.tagline)}</p>
                      <span className="inline-block mt-4 text-[0.6rem] uppercase tracking-[0.3em] text-white/90 border border-white/40 px-4 py-2 group-hover:bg-gold group-hover:border-gold group-hover:text-black transition-colors duration-500">{tr("Explore")}</span>
                    </div>
                  </div>
                </Link>
              </Reveal>
            ))}
          </div>
          <button
            data-testid="services-carousel-next"
            onClick={() => scrollCarousel(1)}
            aria-label={tr("Next services")}
            className="hidden lg:flex absolute right-6 top-[38%] z-10 w-12 h-12 rounded-full bg-white shadow-xl items-center justify-center text-navy hover:bg-gold hover:text-black transition-colors duration-300"
          >
            <ChevronRight strokeWidth={1.5} size={20} />
          </button>
        </div>
      </section>

      {/* ——— MEMBERSHIP TEASER ——— */}
      <section className="relative py-28 lg:py-40 overflow-hidden grain">
        <div className="absolute inset-0">
          <img src={IMAGES.jet} alt="" className="w-full h-full object-cover opacity-25" />
          <div className="absolute inset-0 bg-gradient-to-b from-ink via-ink/70 to-ink" />
        </div>
        <div className="relative max-w-4xl mx-auto px-6 text-center">
          <Reveal>
            <img src="/logo-gold.png" alt="" className="h-24 w-auto mx-auto mb-10 opacity-90" />
          </Reveal>
          <Reveal delay={0.1}>
            <Overline>{tr("Membership")}</Overline>
          </Reveal>
          <Reveal delay={0.2}>
            <h2 className="font-serif font-light text-4xl sm:text-5xl lg:text-6xl leading-[1.05] mt-6">{tr("One of life's most")}<em className="gold-shimmer italic">{tr("high-powered networks.")}</em>
            </h2>
          </Reveal>
          <Reveal delay={0.3}>
            <p className="mt-8 text-white/60 font-light leading-relaxed text-lg max-w-2xl mx-auto">{tr("No matter the request, we will deliver. Membership is limited and granted by application only.")}</p>
          </Reveal>
          <Reveal delay={0.4}>
            <Link
              to="/membership"
              data-testid="home-membership-cta"
              className="inline-block mt-12 bg-gold text-black text-xs uppercase tracking-[0.3em] px-10 py-4 hover:bg-gold-bright transition-colors duration-400"
            >{tr("Discover Membership")}</Link>
          </Reveal>
        </div>
      </section>

      {/* ——— OUR PROMISE ——— */}
      <section className="bg-ivory text-navy py-24 lg:py-32">
        <div className="max-w-7xl mx-auto px-6 lg:px-12 grid grid-cols-1 lg:grid-cols-12 gap-14 items-center">
          <div className="lg:col-span-7">
            <Reveal>
              <p className="text-[0.7rem] uppercase tracking-[0.35em] text-navy/50 font-medium">{tr("Our Promise!")}</p>
              <h2 className="font-serif font-light text-4xl sm:text-5xl leading-tight mt-5">{tr("Exceptional service,")}<em className="italic">{tr("without compromise.")}</em>
              </h2>
              <p className="mt-7 text-navy/70 font-light leading-relaxed max-w-xl">{tr("At Delux Concierge, our promise is clear: to provide exceptional service without compromise. Every experience we curate is grounded in trust, discretion, and an uncompromising standard of quality. From start to finish, we ensure every detail reflects the excellence you expect and deserve.")}</p>
              <div className="mt-8">
                <img src="/signature.png" alt={tr("Signature of Mr. Roberts")} className="h-20 w-auto mix-blend-multiply" />
                <p className="mt-2 font-serif text-xl">{tr("Mr. Roberts")}</p>
                <p className="text-[0.62rem] uppercase tracking-[0.3em] text-navy/50 mt-1">{tr("Delux Concierge CEO")}</p>
              </div>
            </Reveal>
          </div>
          <div className="lg:col-span-4 lg:col-start-9">
            <Reveal delay={0.15}>
              <ImageFader images={PROMISE_SLIDES} alt={tr("The Delux standard")} className="aspect-[3/4]" testIdPrefix="promise-slide" />
            </Reveal>
          </div>
        </div>
      </section>

      {/* ——— TESTIMONIALS ——— */}
      <Testimonials />

      {/* ——— FINAL CTA ——— */}
      <section className="relative py-32 lg:py-44 overflow-hidden grain">
        <div className="absolute inset-0">
          <img src={IMAGES.cta} alt="" className="w-full h-full object-cover" />
          <div className="absolute inset-0 bg-ink/70" />
        </div>
        <div className="relative max-w-4xl mx-auto px-6 text-center">
          <Reveal>
            <Overline>{tr("One Network. Endless Possibilities.")}</Overline>
          </Reveal>
          <Reveal delay={0.1}>
            <h2 className="font-serif font-light text-4xl sm:text-5xl lg:text-6xl leading-[1.08] mt-6">{tr("Let's write your next chapter —")}<em className="gold-shimmer italic">{tr("wherever in the world it may be.")}</em>
            </h2>
          </Reveal>
          <Reveal delay={0.2}>
            <p className="mt-7 text-white/60 font-light max-w-xl mx-auto">{tr("Whatever the request, our team responds with discretion and care.")}</p>
          </Reveal>
          <Reveal delay={0.3}>
            <div className="mt-12 flex flex-wrap justify-center gap-5">
              <Link
                to="/contact"
                data-testid="home-cta-contact"
                className="group bg-gold text-black text-xs uppercase tracking-[0.3em] px-10 py-4 flex items-center gap-3 hover:bg-gold-bright transition-colors duration-400"
              >{tr("Get In Touch")}<ArrowRight strokeWidth={1.5} size={15} className="transition-transform duration-400 group-hover:translate-x-1" />
              </Link>
              <Link
                to="/services"
                data-testid="home-cta-services"
                className="group border border-white/40 text-white text-xs uppercase tracking-[0.3em] px-10 py-4 flex items-center gap-3 hover:border-gold hover:text-gold transition-colors duration-400"
              >{tr("Explore Services")}<ArrowRight strokeWidth={1.5} size={15} className="transition-transform duration-400 group-hover:translate-x-1" />
              </Link>
            </div>
          </Reveal>
        </div>
      </section>
    </div>
  );
}
